import { motion, Variants } from "framer-motion";
import { useEffect, useMemo, useState } from "react";

interface CoinJumpAnimationProps {
  isVisible: boolean;
  effectCount?: number;
  src?: string;
  maxLandingX?: number;
  spawnYOffset?: number;
  coinSize?: number;
  landingYMin?: number;
  landingYMax?: number;
}

type Shot = {
  phase: 0 | 1;
  delay: number;
  angleDeg: number;
  originX: number;
  originY: number;
  speed: number;
  id: string;
};

const COIN_DURATION = 0.95;
const GRAVITY = 1600;

const PHASE_GAP = -0.30;
const CYCLE_PAUSE = -0.3;


const JET_INTERVAL = 0.2;

const T_JET1 = 0.0;
const T_JET2 = T_JET1 + JET_INTERVAL;
const T_JET3 = T_JET2 + JET_INTERVAL;
const T_JET4 = T_JET3 + JET_INTERVAL;


const JET4_ORIGIN_OFFSET_X = 24;


const SPEED_JET1 = 620;
const SPEED_JET2 = 640;
const SPEED_JET3 = 660;
const SPEED_JET4 = 680;


const LANDING_Y_MIN = 175;
const LANDING_Y_MAX = 235;


const deg2rad = (deg: number) => (deg * Math.PI) / 180;
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const fract = (x: number) => x - Math.floor(x);
const seeded01 = (seed: number) => fract(Math.sin(seed) * 10000);

function buildTrajectory(params: {
  angleDeg: number;
  speed: number;
  originX: number;
  originY: number;
  mirror: boolean;
  maxLandingX: number;
  seed: number;
  landingYMin: number;
  landingYMax: number;
}) {
  const { angleDeg, speed, originX, originY, mirror, maxLandingX, seed, landingYMin, landingYMax } = params;


  const mirrorSign = mirror ? -1 : 1;

  const x0 = originX * mirrorSign;
  const y0 = originY;


  const a = deg2rad(angleDeg);
  const vx = Math.cos(a) * speed * mirrorSign;
  const vy = -Math.sin(a) * speed;

  const t = COIN_DURATION * (0.96 + seeded01(seed * 7.7) * 0.08);

  let xEnd = x0 + vx * t;
  let yEnd = y0 + vy * t + 0.5 * GRAVITY * t * t;

  const yTarget = lerp(landingYMin, landingYMax, seeded01(seed * 19.9));
  yEnd = lerp(yEnd, yTarget, 0.65);

  xEnd = clamp(xEnd, -maxLandingX, maxLandingX);

  const tPeak = clamp(-vy / GRAVITY, 0, t);
  const xPeak = x0 + vx * tPeak;
  const yPeak = y0 + vy * tPeak + 0.5 * GRAVITY * tPeak * tPeak;

  return { x0, y0, xPeak, yPeak, xEnd, yEnd };
}

function createVariants(args: {
  shot: Shot;
  phaseOffset: number;
  maxLandingX: number;
  repeat: number | "Infinity";
  repeatDelay: number;
  landingYMin: number;
  landingYMax: number;
}) {
  const { shot, phaseOffset, maxLandingX, repeat, repeatDelay, landingYMin, landingYMax } = args;

  const seedNum = Number(shot.id.replace(/\D/g, "")) || 123;

  const traj = buildTrajectory({
    angleDeg: shot.angleDeg,
    speed: shot.speed,
    originX: shot.originX,
    originY: shot.originY,
    mirror: shot.phase === 1,
    maxLandingX,
    seed: seedNum,
    landingYMin,
    landingYMax,
  });

  const totalDelay = phaseOffset + shot.delay;

  const v: Variants = {
    idle: { opacity: 0, x: traj.x0, y: traj.y0, rotate: 0, scale: 1 },
    active: {
      opacity: [0, 1, 1, 0],
      x: [traj.x0, traj.xPeak, traj.xEnd],
      y: [traj.y0, traj.yPeak, traj.yEnd],
      rotate: [0, 0],
      scale: 1,
      transition: {
        duration: COIN_DURATION,
        times: [0, 0.05, 0.98, 1],
        ease: ["easeOut", "easeIn"],
        delay: totalDelay,
        repeat: repeat === "Infinity" ? Infinity : repeat,
        repeatType: "loop",
        repeatDelay,
      },
    },
  };

  return v;
}

const CoinJumpAnimation = ({
  isVisible,
  effectCount = Infinity,
  src = "/images/icon-coin-2.png",
  maxLandingX = 70,
  spawnYOffset = 8,
  coinSize = 24,
  landingYMin = LANDING_Y_MIN,
  landingYMax = LANDING_Y_MAX,
}: CoinJumpAnimationProps) => {
  const [shouldRender, setShouldRender] = useState(false);
  const [isActive, setIsActive] = useState(false);

  const shots: Shot[] = useMemo(() => {
    const base: Omit<Shot, "phase">[] = [

      { delay: T_JET1, angleDeg: 80, originX: 0, originY: spawnYOffset, speed: SPEED_JET1, id: "p1j1a80" },
      { delay: T_JET1, angleDeg: 100, originX: 0, originY: spawnYOffset, speed: SPEED_JET1, id: "p1j1a100" },


      { delay: T_JET2, angleDeg: 96, originX: 0, originY: spawnYOffset, speed: SPEED_JET2, id: "p1j2a95" },


      { delay: T_JET3, angleDeg: 84, originX: 0, originY: spawnYOffset, speed: SPEED_JET3, id: "p1j3a110" },


      { delay: T_JET4, angleDeg: 100, originX: JET4_ORIGIN_OFFSET_X, originY: spawnYOffset, speed: SPEED_JET4, id: "p1j4a70" },
    ];

    const phase1: Shot[] = base.map((s) => ({ ...s, phase: 0 as const }));
    const phase2: Shot[] = base.map((s) => ({ ...s, phase: 1 as const, id: s.id.replace("p1", "p2") }));

    return [...phase1, ...phase2];
  }, [spawnYOffset]);


  const phaseMaxDelay = Math.max(T_JET1, T_JET2, T_JET3, T_JET4);
  const phaseLength = phaseMaxDelay + COIN_DURATION;


  const phase2Offset = phaseLength + PHASE_GAP;


  const cycleLength = phase2Offset + phaseLength + CYCLE_PAUSE;

  const repeat =
    effectCount === Infinity ? ("Infinity" as const) : Math.max(0, Math.floor(effectCount) - 1);

  const repeatDelay = Math.max(0, cycleLength - COIN_DURATION);

  const coinData = useMemo(() => {
    return shots.map((shot) => {
      const phaseOffset = shot.phase === 0 ? 0 : phase2Offset;
      return {
        key: shot.id,
        variants: createVariants({ shot, phaseOffset, maxLandingX, repeat, repeatDelay, landingYMin, landingYMax }),
      };
    });
  }, [shots, phase2Offset, maxLandingX, repeat, repeatDelay, landingYMin, landingYMax]);

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      setIsActive(true);
    } else {
      setIsActive(false);
      const t = setTimeout(() => setShouldRender(false), 400);
      return () => clearTimeout(t);
    }
  }, [isVisible]);

  if (!shouldRender) return null;

  return (
    <div
      className="pointer-events-none top-10 absolute left-1/2 h-full w-full -translate-x-1/2 -ml-4"
      style={{
        zIndex: -1,
        bottom: "calc(100% + 16px)",
        overflow: "visible",
      }}
    >
      {coinData.map((c) => (
        <motion.img
          key={c.key}
          src={src}
          alt=""
          className="absolute"
          style={{
            width: coinSize,
            height: coinSize,
            left: "50%",
            top: 0,
            objectFit: "contain",
            willChange: "transform, opacity",
            transformOrigin: "center center",
          }}
          variants={c.variants}
          initial="idle"
          animate={isActive ? "active" : "idle"}
          aria-hidden="true"
        />
      ))}
    </div>
  );
};

export default CoinJumpAnimation;
